import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import styles from '../styles/pages/MatchOfferList.module.css';
import { MatchResultMobile } from 'components/mobile/MatchResultMobile/MatchResultMobile';
import { Navigate, useLocation } from 'react-router-dom';
import { capitalizeFirstWord } from 'helpers/stringHelper';
import { useMemo, useState } from 'react';
import { MatchOffer } from 'types/MatchOffer';
import useAsyncEffect from 'use-async-effect';
import { getResultsEventsByLeague } from 'services/api/sportBetsService';
import { Loader } from 'components/Loader/Loader';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useTranslation } from 'react-i18next';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { SportIDs } from 'types/enums';

/** Mobile: one league’s finished/live results (scores), not betting quotes. */
export const ResultsMatchOfferList = () => {
  const [matches, setMatches] = useState<MatchOffer[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation();
  const location = useLocation();
  const urlParts = location.pathname.split('/');

  const leagueId = urlParts.pop();
  const leagueName = decodeURI(urlParts.pop()!);
  const sportId = urlParts.pop();
  const sportName = decodeURI(urlParts.pop()!);
  const sportIdNumber = useMemo(() => Number.parseInt(sportId!, 10), [sportId]);

  const includeFisFromUrl = useMemo(() => {
    const p = new URLSearchParams(location.search || '');
    const raw = p.get('fi') || p.get('include_fi');
    if (!raw) return undefined;
    const ids = raw
      .split(',')
      .map((s) => parseInt(String(s).trim(), 10))
      .filter((n) => !Number.isNaN(n) && n > 0);
    return ids.length ? ids : undefined;
  }, [location.search]);

  const sortedMatches = useMemo(() => {
    if (!matches) return [];
    return [...matches].sort((a, b) => a.time - b.time);
  }, [matches]);

  useAsyncEffect(async () => {
    setIsLoading(true);
    setMatches(
      await getResultsEventsByLeague(
        Number.parseInt(leagueId!, 10),
        sportIdNumber as SportIDs,
        true,
        includeFisFromUrl
      )
    );
    setIsLoading(false);
  }, [leagueId, sportIdNumber, includeFisFromUrl]);

  if (!isMobile) {
    return <Navigate to="/not-found" />;
  }

  return (
    <MobileLayout pageTitle={capitalizeFirstWord(leagueName!)}>
      <div className={styles.wrapper}>
        <div className={styles.leagueInfoWrapper}>
          <SportIcon
            className={styles.sportIcon}
            sportId={sportId}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = getDefaultSportIconSrc();
            }}
            alt={sportName}
          />
          <span>
            {capitalizeFirstWord(sportName!)} - {leagueName}
          </span>
        </div>
        <div className={styles.matches}>
          {isLoading && <Loader />}
          {!isLoading &&
            sortedMatches.length > 0 &&
            sortedMatches.map((m) => (
              <MatchResultMobile key={m.fi} match={m} sportId={sportIdNumber} />
            ))}
          {!isLoading && sortedMatches.length === 0 && (
            <div className={styles.empty}>
              {t('no_results_label', 'No results yet.')}
            </div>
          )}
        </div>
      </div>
    </MobileLayout>
  );
};
